import { useEffect } from "react";
import { ROUTE_SEO } from "../data/routeSeo";
import { ORG_ID, WEBSITE_ID, organizationSchema, websiteSchema } from "../data/schema";
import { SITE, canonicalFor, abs } from "../lib/site";

const DEFAULT_IMAGE = `${SITE}/og-image.png`;

const upsert = (tag, key, value, attrs) => {
  let el = document.head.querySelector(`${tag}[${key}="${value}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

/**
 * Title, description, canonical, robots, Open Graph / Twitter tags and one
 * JSON-LD @graph per page, written straight into <head>. Titles and
 * descriptions come from ROUTE_SEO (data/routeSeo.js) when the route has an
 * entry, otherwise from props. URLs always use the production host, so a
 * preview deployment never declares itself canonical.
 *
 * scripts/prerender.js runs every route at build time and saves the resulting
 * <head>, so these tags are in the HTML before any JavaScript runs.
 */
export default function SEO({ title, description, path = "/", schema, image, type = "website", noindex = false, breadcrumbs }) {
  const route = path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  const preset = ROUTE_SEO[route] || {};
  const rawTitle = preset.title || title || "Grotech Web";
  // Add the brand only when the result still fits in a 60-character search title.
  const fullTitle = /Grotech/.test(rawTitle) || rawTitle.length > 46 ? rawTitle : `${rawTitle} | Grotech Web`;
  const desc = preset.description || description || "";
  const url = canonicalFor(route);
  const img = abs(image) || DEFAULT_IMAGE;
  const schemaKey = JSON.stringify(schema || null) + JSON.stringify(breadcrumbs || null);

  useEffect(() => {
    document.title = fullTitle;
    upsert("meta", "name", "description", { content: desc });
    upsert("meta", "name", "robots", {
      content: noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });
    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (noindex) {
      if (canonical) canonical.remove();
    } else {
      upsert("link", "rel", "canonical", { href: url });
    }
    upsert("meta", "property", "og:site_name", { content: "Grotech Web" });
    upsert("meta", "property", "og:locale", { content: "en_US" });
    upsert("meta", "property", "og:type", { content: type });
    upsert("meta", "property", "og:title", { content: fullTitle });
    upsert("meta", "property", "og:description", { content: desc });
    upsert("meta", "property", "og:url", { content: url });
    upsert("meta", "property", "og:image", { content: img });
    upsert("meta", "property", "og:image:alt", { content: fullTitle });
    upsert("meta", "name", "twitter:card", { content: "summary_large_image" });
    upsert("meta", "name", "twitter:title", { content: fullTitle });
    upsert("meta", "name", "twitter:description", { content: desc });
    upsert("meta", "name", "twitter:image", { content: img });

    // One graph per page: shared WebSite + organisation entities, this page,
    // its breadcrumb trail and any page-specific entities passed in `schema`.
    const graph = [websiteSchema, organizationSchema];
    if (!noindex) {
      graph.push({
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: fullTitle,
        description: desc,
        isPartOf: { "@id": WEBSITE_ID },
        about: { "@id": ORG_ID },
        inLanguage: "en",
      });
      const trail = breadcrumbs || (route !== "/" ? [{ name: "Home", path: "/" }, { name: fullTitle.split(" | ")[0], path: route }] : null);
      if (trail) {
        graph.push({
          "@type": "BreadcrumbList",
          itemListElement: trail.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name, item: canonicalFor(b.path) })),
        });
      }
    }
    for (const s of [].concat(schema || [])) {
      const { "@context": _ctx, ...rest } = s;
      graph.push(rest);
    }
    document.querySelectorAll("script[data-seo-schema]").forEach((s) => s.remove());
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-schema", "true");
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, desc, url, img, type, noindex, schemaKey]);
  return null;
}
